import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, BarChart3, Users, Activity, TrendingUp, Repeat, Building2, ArrowRight } from "lucide-react";
import SystemHealth from "./SystemHealth";

const fmtMoney = (dollars, currency) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "aud").toUpperCase(),
      maximumFractionDigits: 2,
    }).format(dollars || 0);
  } catch {
    return `$${(dollars || 0).toFixed(2)}`;
  }
};

const Stat = ({ label, value, sub, Icon, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-[#0e1726] p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-[#5ec0ff]" />}
    </div>
    <div className="text-3xl font-bold text-white mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const TIER_META = {
  pro: { label: "Pro", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  team: { label: "Team", cls: "border-sky-500/40 bg-sky-500/10 text-sky-300" },
  comp: { label: "Complimentary", cls: "border-violet-500/40 bg-violet-500/10 text-violet-300" },
  free: { label: "Free", cls: "border-slate-600 bg-slate-700/40 text-slate-300" },
};
const TIER_ORDER = ["pro", "team", "comp", "free"];

export default function AdminOverview() {
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/api/admin/metrics").then(({ data }) => setM(data)).catch(() => setM(false));
  }, []);

  const tiers = m && m.users_by_tier ? m.users_by_tier : {};
  const paying = m ? (tiers.pro || 0) + (m.active_teams || 0) : 0;

  return (
    <div data-testid="admin-overview-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
        <BarChart3 className="w-3.5 h-3.5" /> Overview
      </div>
      <h1 className="text-2xl font-bold text-white">Platform metrics</h1>
      <p className="text-sm text-slate-400 mt-1">Live usage and revenue across all trainees and simulators.</p>

      {m === null ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-16">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading metrics…
        </div>
      ) : m === false ? (
        <div className="text-center text-slate-500 py-16 text-sm">Couldn’t load metrics.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Stat label="Total users" value={m.total_users} sub={`${m.users_by_role?.admin || 0} admin · ${m.users_by_role?.instructor || 0} instructor`} Icon={Users} testid="metric-total-users" />
            <Stat label="MRR" value={fmtMoney(m.mrr, m.currency)} sub="recurring / month" Icon={Repeat} testid="metric-mrr" />
            <Stat label="Paying subscriptions" value={paying} sub={`${tiers.pro || 0} Pro · ${m.active_teams || 0} team${m.active_teams === 1 ? "" : "s"}`} Icon={Building2} testid="metric-paying" />
            <Stat label="Sessions saved" value={m.total_sessions} sub={`${m.sessions_7d || 0} in last 7 days`} Icon={Activity} testid="metric-total-sessions" />
          </div>

          {/* subscription tier breakdown */}
          <div className="mt-6 rounded-xl border border-slate-800 bg-[#0e1726] p-4" data-testid="metric-tiers">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Subscription tiers</span>
              <Link to="/admin/subscribers" className="text-[11px] text-[#5ec0ff] hover:underline inline-flex items-center gap-1" data-testid="overview-subscribers-link">
                Revenue detail <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {TIER_ORDER.map((k) => (
                <div key={k} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${TIER_META[k].cls}`} data-testid={`tier-${k}`}>
                  <span className="text-lg font-bold font-mono-clinical">{tiers[k] || 0}</span>
                  <span className="text-[11px] uppercase tracking-wide font-semibold">{TIER_META[k].label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/40 text-slate-300 px-3 py-2" data-testid="tier-new-7d">
                <span className="text-lg font-bold font-mono-clinical">{m.new_users_7d || 0}</span>
                <span className="text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> New · 7d</span>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-slate-800 bg-[#0e1726] overflow-hidden" data-testid="metric-top-sims">
            <div className="px-4 py-3 border-b border-slate-800 text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Top simulators by completion
            </div>
            {(m.top_simulators || []).length === 0 ? (
              <div className="text-center text-slate-500 py-10 text-sm">No sessions yet.</div>
            ) : (
              <div className="divide-y divide-slate-800">
                {m.top_simulators.map((s, i) => (
                  <div key={s.simulator || i} className="flex items-center justify-between px-4 py-3" data-testid={`top-sim-${i}`}>
                    <span className="text-sm text-slate-200 truncate">{s.simulator || "—"}</span>
                    <div className="flex items-center gap-6 shrink-0">
                      <span className="text-xs text-slate-500">avg <span className="text-slate-300 font-mono-clinical">{s.avg_score}</span></span>
                      <span className="text-sm font-bold text-white font-mono-clinical">{s.completions}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <SystemHealth />
        </>
      )}
    </div>
  );
}
